import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import EditDistributionModal from './edit-distribution-modal';
import vendorPriorityService from './vendor-priority-service';
import { VendorType, PhlebotVendor } from './enums';
import { TableWrapper, SectionTitle, ModalHeader } from './styled-components';
import { 
    IVendorPriority, 
    IVendorPriorityGroup, 
    IVendorPriorityModalProps,
    IVendorDistribution 
} from './types';
import { getVendorName, getEntityTypeName, getVendorTypeName } from './helpers';
import { showSuccessToast, showErrorToast } from './utils/toast-helpers';
import { useVendorPriorities } from './hooks/useVendorPriorities';
import VendorTypeTable from './components/VendorTypeTable';

const VendorPriorityModal: React.FC<IVendorPriorityModalProps> = ({ show, vendorPriorities, shipVendors, isLoading, onCancel, onSave }) => {
    const [priorities, setPriorities] = useState<IVendorPriority[]>([]);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [editingGroup, setEditingGroup] = useState<IVendorPriorityGroup | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showMainModal, setShowMainModal] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Use the custom hook to get grouped priorities and vendor IDs
    const { 
        groupedByVendorType, 
        vendorIdsByType,
        findPriority 
    } = useVendorPriorities(priorities);

    // Initialize priorities when modal opens
    useEffect(() => {
        if (show) {
            setPriorities([...vendorPriorities]);
            setValidationError(null);
            setEditingGroup(null);
            setShowEditModal(false);
            setShowMainModal(true);
            setIsSaving(false);
        }
    }, [show, vendorPriorities]);

    // Start editing a group
    const handleStartEdit = (group: IVendorPriorityGroup) => {
        setEditingGroup(group);
        setShowEditModal(true);
        setShowMainModal(false); // Hide the main modal when opening the edit modal
    };

    // Get priorities for a specific group
    const getGroupPriorities = (group: IVendorPriorityGroup): IVendorPriority[] => {
        return priorities.filter(
            p => p.entityType === group.entityType && p.vendorType === group.vendorType
        );
    };
    
    // Update priorities with new distribution values
    const updatePrioritiesWithDistribution = (
        groupPriorities: IVendorPriority[], 
        distributions: IVendorDistribution[]
    ): IVendorPriority[] => {
        return groupPriorities.map(p => {
            const distribution = distributions.find(d => d.vendorId === p.vendorId);
            if (distribution) {
                return { ...p, odds: distribution.odds };
            }
            return p;
        });
    };
    
    // Update local state with updated priorities
    const updateLocalPriorities = (updatedPriorities: IVendorPriority[]): IVendorPriority[] => {
        return priorities.map(p => {
            const updated = updatedPriorities.find(up => up.id === p.id);
            return updated || p;
        });
    };

    // Handle saving distribution from edit modal
    const handleSaveDistribution = async (distributions: IVendorDistribution[]) => {
        if (!editingGroup) return;
        
        try {
            setIsSaving(true);
            
            // Get and update priorities
            const groupPriorities = getGroupPriorities(editingGroup);
            const updatedPriorities = updatePrioritiesWithDistribution(groupPriorities, distributions);
            
            // Save each priority individually using the API
            const savePromises = updatedPriorities.map(priority => 
                vendorPriorityService.updateVendorPriority(priority)
            );
            
            // Wait for all updates to complete
            await Promise.all(savePromises);
            
            // Update the local state
            const newPriorities = updateLocalPriorities(updatedPriorities);
            
            setPriorities(newPriorities);
            setEditingGroup(null);
            setShowEditModal(false);
            setShowMainModal(true); // Show the main modal again
            setValidationError(null);
            
            // Show success toast notification
            showSuccessToast(`${getEntityTypeName(editingGroup.entityType)} distribution updated successfully!`);
            
            // Refresh the full list to ensure we have the latest data
            onSave(newPriorities);
        } catch (error) {
            showErrorToast(`Failed to save distribution: ${error.message}`);
            
            setValidationError(`Failed to save distribution: ${error.message}`);
            setShowMainModal(true);
            setShowEditModal(false);
        } finally {
            setIsSaving(false);
        }
    };

    // Handle cancel edit
    const handleCancelEdit = () => {
        setEditingGroup(null);
        setShowEditModal(false);
        setShowMainModal(true); // Show the main modal again
    };

    // Check if a group's total odds equals 100%
    const isGroupValid = (group: IVendorPriorityGroup): boolean => {
        const totalOdds = group.priorities.reduce((sum, p) => sum + p.odds, 0);
        return totalOdds === 100;
    };
    
    // Get error message for invalid group
    const getGroupErrorMessage = (group: IVendorPriorityGroup): string => {
        const totalOdds = group.priorities.reduce((sum, p) => sum + p.odds, 0);
        return `The total percentage for ${getEntityTypeName(group.entityType)} must equal 100% (currently ${totalOdds}%)`;
    };

    // Validate groups totals
    const validateGroups = (): boolean => {
        for (const vendorTypeKey in groupedByVendorType) {
            for (const group of groupedByVendorType[vendorTypeKey]) {
                if (!isGroupValid(group)) {
                    const errorMessage = getGroupErrorMessage(group);
                    showErrorToast(errorMessage);
                    setValidationError(errorMessage);
                    return false;
                }
            }
        }
        return true;
    };

    // Handle save all changes
    const handleSave = async () => {
        if (validateGroups()) {
            try {
                await onSave(priorities);
                showSuccessToast("All vendor distributions updated successfully!");
            } catch (error) {
                const errorMessage = `Failed to save vendor distributions: ${error.message}`;
                showErrorToast(errorMessage);
                setValidationError(errorMessage);
            }
        }
    };

    // Use the vendor name helper with the current shipVendors
    const getVendorNameWithContext = (vendorId: number, vendorType?: number): string => {
        return getVendorName(vendorId, shipVendors, vendorType);
    };

    // Prepare distribution data for edit modal
    const getDistributionsForEdit = (): IVendorDistribution[] => {
        if (!editingGroup) return [];
        
        const vendorTypeKey = VendorType[editingGroup.vendorType] || `Unknown (${editingGroup.vendorType})`;
        const vendorIds = vendorIdsByType[vendorTypeKey] || [];
        
        return vendorIds.map(vendorId => {
            const priority = findPriority(editingGroup, vendorId);
            return {
                id: priority?.id || 0,
                vendorId,
                name: getVendorNameWithContext(vendorId, editingGroup.vendorType),
                odds: priority?.odds || 0
            };
        });
    };

    return (
        <>
            <Modal 
                show={show && showMainModal} 
                backdrop="static" 
                bsSize="large" 
                onHide={onCancel}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <span className="glyphicon glyphicon-stats"></span> Vendor Distribution Management
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isLoading && !priorities.length && (
                        <div className="text-center" style={{ padding: '20px' }}>
                            <h4>Loading vendor distributions...</h4>
                        </div>
                    )}

                    <ModalHeader>
                        <h4>
                            <span className="glyphicon glyphicon-stats"></span> Vendor Distribution Settings
                        </h4>
                        <p>
                            This table shows the current distribution percentages for each vendor by entity type. 
                            Click the Edit button to modify the distribution for a specific entity type. 
                            The total percentage for each row must equal 100%.
                        </p>
                    </ModalHeader>

                    {validationError && (
                        <Alert bsStyle="danger">{validationError}</Alert>
                    )}

                    {Object.keys(groupedByVendorType).length === 0 ? (
                        <Alert bsStyle="info">No vendor distributions found.</Alert>
                    ) : (
                        Object.entries(groupedByVendorType)
                            .sort(([keyA], [keyB]) => {
                                // Sort KIT first, then PHLEBOT
                                if (keyA === 'KIT') return -1;
                                if (keyB === 'KIT') return 1;
                                return keyA.localeCompare(keyB);
                            })
                            .map(([vendorTypeKey, groups]) => (
                                <VendorTypeTable
                                    key={vendorTypeKey}
                                    vendorTypeKey={vendorTypeKey}
                                    groups={groups}
                                    vendorIds={vendorIdsByType[vendorTypeKey] || []}
                                    getVendorName={getVendorNameWithContext}
                                    findPriority={findPriority}
                                    onStartEdit={handleStartEdit}
                                    isLoading={isLoading}
                                    showEditModal={showEditModal}
                                    isSaving={isSaving}
                                />
                            ))
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={onCancel} disabled={isLoading}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Distribution Modal */}
            {editingGroup && (
                <EditDistributionModal
                    show={showEditModal}
                    entityName={getEntityTypeName(editingGroup.entityType)}
                    distributions={getDistributionsForEdit()}
                    onSave={handleSaveDistribution}
                    onCancel={handleCancelEdit}
                />
            )}
        </>
    );
};

export default VendorPriorityModal;